// アイテム名/価値/単位セルの共通コンポーネント（計算ビュー・測定ビューで共用）。
// 行モデルは PricedRow（ValueRow と GroupMember の共通部分）。
// - itemId あり＋相場あり = 自動（相場連動の読取専用表示＋手動上書きボタン）
// - itemId なし = 手動（value 入力）
import { fromBase } from '../../currency/convert';
import type { RateTable } from '../../currency/rate-table';
import { el } from '../dom';
import { fmtNum } from '../format';

/** アイテム名/価値/単位を共通操作するための最小型（ValueRow と GroupMember の共通部分）。 */
export type PricedRow = { name: string; itemId?: string; unit: string; value: number };

export interface PickerDeps {
  /** 現在のレート表（イベント発火時点の値を返すこと）。 */
  rt(): RateTable | null;
  /** 行フィールドの更新（計算ビューでは store.updateSet）。 */
  onMutate(fn: () => void): void;
  /** 構造が変わる更新の後に呼ぶ（計算ビューでは renderInputs）。 */
  onStructure(): void;
  /** カタログ datalist の id。両ビューが常時 DOM に居るためビューごとに一意にする。 */
  datalistId: string;
}

/** 数値入力のパース（非負・不正は fallback）。 */
export function num(v: string, fallback = 0): number {
  const n = parseFloat(v);
  return Number.isFinite(n) ? Math.max(0, n) : fallback;
}

// アイテム名: カタログ検索付きコンボボックス（datalist）。一致→自動相場 / 不一致→手動。
export function itemNameInput(row: PricedRow, deps: PickerDeps): HTMLElement {
  return el('input', {
    type: 'text', class: 'item-name', list: deps.datalistId, value: row.name, placeholder: 'アイテム名（入力 or 選択）',
    oninput: (e) => deps.onMutate(() => { row.name = (e.target as HTMLInputElement).value; }),
    onchange: (e) => commitItemName(row, (e.target as HTMLInputElement).value, deps),
  });
}

function commitItemName(row: PricedRow, value: string, deps: PickerDeps): void {
  const match = deps.rt()?.findByText(value);
  deps.onMutate(() => {
    if (match) { row.itemId = match.id; row.name = match.text; }
    else { row.itemId = undefined; row.name = value; }
  });
  deps.onStructure();
}

// 価値: 自動（itemId かつ相場あり）は読取専用で単位換算表示＋手動上書き / 手動は入力。
export function valueCell(row: PricedRow, deps: PickerDeps): HTMLElement {
  const rt = deps.rt();
  const isAuto = !!row.itemId && !!rt && rt.has(row.itemId);
  if (isAuto && rt) {
    const baseVal = rt.rate(row.itemId!) ?? 0;
    const shown = fromBase(baseVal, row.unit, rt);
    return el('div', { class: 'val-auto' }, [
      el('span', { class: 'auto-dot', title: '相場連動（自動）' }),
      el('span', { class: 'val-num', title: '相場連動' }, fmtNum(shown)),
      el('button', { class: 'iconbtn tiny', title: '価格を手動で上書き', onclick: () => overrideManual(row, deps) }, '✎'),
    ]);
  }
  const missing = !!row.itemId && !isAuto;
  return el('input', {
    type: 'number', class: `count val-input ${missing ? 'warn' : ''}`, value: String(row.value),
    title: missing ? 'このアイテムのレートが未取得。手動で価値を入力' : '1個あたりの価値（手動）',
    oninput: (e) => deps.onMutate(() => { row.value = num((e.target as HTMLInputElement).value); row.itemId = undefined; }),
  });
}

function overrideManual(row: PricedRow, deps: PickerDeps): void {
  const rt = deps.rt();
  deps.onMutate(() => {
    const base = rt?.rate(row.itemId ?? '') ?? 0;
    row.value = rt ? (fromBase(base, row.unit, rt) ?? 0) : 0;
    row.itemId = undefined;
  });
  deps.onStructure();
}

// 単位セル: 変更で自動価値の表示が変わるため onStructure を呼ぶ。
export function unitCell(row: PricedRow, deps: PickerDeps): HTMLElement {
  return unitSelect(deps.rt(), row.unit, (id) => { deps.onMutate(() => { row.unit = id; }); deps.onStructure(); });
}

// 表示単位／手動 denomination（オーブ3種, §4.1）。
export function unitSelect(rt: RateTable | null, selected: string, onChange: (id: string) => void): HTMLSelectElement {
  const sel = el('select', { class: 'unit', onchange: (e) => onChange((e.target as HTMLSelectElement).value) }) as HTMLSelectElement;
  if (!rt) {
    sel.append(el('option', { value: selected }, selected || '—'));
    sel.disabled = true;
    return sel;
  }
  const units = rt.displayUnits();
  for (const entry of units) sel.append(el('option', { value: entry.id, selected: entry.id === selected }, entry.text || entry.id));
  if (selected && !units.some((u) => u.id === selected)) sel.append(el('option', { value: selected, selected: true }, selected));
  return sel;
}

// カタログ全品の datalist（アイテム名コンボボックスの候補、ビューごとに1個）。
export function buildCatalogDatalist(rt: RateTable | null, id: string): HTMLElement {
  const dl = el('datalist', { id });
  if (rt) {
    // option は value（アイテム名）のみ。テキストやカテゴリを入れると Firefox は
    // そちらを優先表示して value（名前）を隠すため、全ブラウザで名前が見えるよう value 一本にする。
    for (const entry of rt.catalogList()) {
      dl.append(el('option', { value: entry.text || entry.id }));
    }
  }
  return dl;
}
